import { z } from 'zod';
import { ReferralChannelEnum } from '../startParam.js';

/**
 * Схемы журнала событий виральной петли (Веха 8, ARCHITECTURE_PLAN §12, CANON E1).
 *
 * Источник правды:
 *   - threat-model security/threat-models/analytics.md (S.a/S.b — client-subset
 *     enum; контроли #4/#5).
 *   - CANON B1/B2/B4 (анонимность): клиент НЕ управляет userId/identity (поля нет
 *     в `RecordEventBody`); серверные события (`box_created`/`question_sent`/
 *     `answer_created`) клиент эмитить НЕ вправе.
 *
 * Два enum'а намеренно раздельны:
 *   - `LoopEventType` — ПОЛНЫЙ набор 9 типов (что вообще пишется в `LoopEvent`),
 *     используется server-side в `recordEvent` и в агрегатах K-фактора.
 *   - `ClientLoopEventType` — ПОДМНОЖЕСТВО, которое клиент вправе слать через
 *     `POST /events` (контроль #4 — анти-spoofing серверных метрик, S.a).
 *
 * work-035: `ad_start` (рекламная first-touch атрибуция, `POST /users/ad-source`
 * + бот `/start a<code>`) добавлен в `LoopEventTypeEnum` — пишется ИСКЛЮЧИТЕЛЬНО
 * сервером после валидации `AdSource` (клиент код не спуфит валидностью, но
 * писать событие сам не может — `ad_start` НЕ входит в `ClientLoopEventTypeEnum`).
 * Это авторское событие (userId — юзер, пришедший по рекламе, не аноним).
 */

/** Полный набор типов событий петли (что пишется в `LoopEvent.type`). */
export const LoopEventTypeEnum = z.enum([
  'app_opened',
  'box_created',
  'invite_shared_to_story',
  'ask_opened',
  'question_sent',
  'answer_created',
  'answer_shared_to_story',
  'chat_shared',
  'ad_start',
]);
export type LoopEventType = z.infer<typeof LoopEventTypeEnum>;

/**
 * Подмножество типов, которые КЛИЕНТ вправе сигналить через `POST /events`.
 * Серверные (`box_created`/`question_sent`/`answer_created`) пишутся ИСКЛЮЧИТЕЛЬНО
 * из обработчиков соответствующих ручек — клиент их слать не может (zod → 400).
 * (Контроль #4 / threat-model S.a — single-source-of-truth.)
 */
export const ClientLoopEventTypeEnum = z.enum([
  'app_opened',
  'ask_opened',
  'invite_shared_to_story',
  'answer_shared_to_story',
  'chat_shared',
]);
export type ClientLoopEventType = z.infer<typeof ClientLoopEventTypeEnum>;

// boxToken — публичный токен бокса (зеркало questions.ts: min 8 / max 64; резолв
// в boxId серверно, контроль #12). Используем тот же диапазон, что и
// `CreateQuestionBody.boxToken`, чтобы не плодить расхождения.
const boxTokenSchema = z.string().min(8).max(64);

/**
 * Body `POST /events` (Веха 8 M2). Strict — unknown поля отвергаются (CANON D2,
 * контроль #5). НЕТ полей userId/telegramId/fingerprint — identity берётся из
 * initData серверно (контроль #1, threat-model S.b/B.b).
 *
 * `answerId` (опц., work-031 М1) — id ресурса `Answer` (cuid), НЕ identity
 * отправителя (инвариант S.b не нарушается — это ссылка на СУЩЕСТВУЮЩУЮ
 * сущность, не личность). Используется ИСКЛЮЧИТЕЛЬНО для авторской корреляции
 * `answer_shared_to_story` → флип `Answer.sharedToStory` (ownership проверяется
 * серверно per-request, см. `markAnswerSharedToStory`).
 */
export const RecordEventBody = z
  .object({
    type: ClientLoopEventTypeEnum,
    boxToken: boxTokenSchema.optional(),
    channel: ReferralChannelEnum.optional(),
    answerId: z.string().cuid().optional(),
  })
  .strict();
export type RecordEventBody = z.infer<typeof RecordEventBody>;

/**
 * Response `POST /events`. Всегда `{ ok: true }` (CANON B2-style — никакого
 * сигнала про дедуп/no-op/несуществующий бокс, threat-model I.e/контроль #14).
 * `literal(true)` — чтобы future-self не залогировал статус операции в ответ.
 */
export const RecordEventResponse = z
  .object({
    ok: z.literal(true),
  })
  .strict();
export type RecordEventResponse = z.infer<typeof RecordEventResponse>;
